import { GetStaticProps, InferGetServerSidePropsType, NextPage } from "next";
import { PageMeta, PostMetadata } from "~/components/PageMeta";
import { apiVerticalToPageMetaData } from "~/components/PageMeta/PageMeta";
import { apiToReadMorePosts } from "~/components/ReadMorePosts/apiToReadMorePosts";

import {
  apiToContentItemProps,
  VerticalPage,
  VerticalPageProps,
} from "~/components/VerticalPage";

import {
  fetchVertical,
  fetchVerticalPreview,
} from "~/lib/server/asgardApiCalls";
import { createGetStaticPathsForPosts } from "~/lib/server/createGetStaticPathsForPosts";
import { fetchPosts } from "~/lib/server/fetchPosts";
import { wrapGetStaticProps } from "~/lib/server/wrapGetProps";
import { heDecodeOrDefault } from "~/lib/shared/html-utils";
import { apiVerticalToContentProps } from "~/pages/verticals/apiToVerticalProps";
import { PostTypeEnum } from "~/types/asgard/PostType";

export type PageParams = {
  verticalsParam: string;
};

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 12;

export const getStaticPaths = createGetStaticPathsForPosts(
  PostTypeEnum.Vertical,
  PostTypeEnum.Vertical,
  "slug"
);

type VerticalPageStaticProps = VerticalPageProps & {
  meta: PostMetadata;
};

export const getStaticProps: GetStaticProps<
  VerticalPageStaticProps,
  PageParams
> = wrapGetStaticProps(async ({ params, preview }) => {
  const { verticalsParam } = params!;
  const fetchMethod = preview ? fetchVerticalPreview : fetchVertical;
  const { data: verticalData } = await fetchMethod(verticalsParam);
  const { posts, totalPages } = await fetchPosts({
    categoryIdFilter: verticalData.primaryCategory.toString(),
    currentPage: DEFAULT_PAGE,
    excludePostIdsFilter: verticalData.excludeIds,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const { columnLeft, columnRight, columnCenter } = verticalData.header;

  return {
    props: {
      headerProps: {
        mainTitle: heDecodeOrDefault(verticalData.title),
        mainBody: heDecodeOrDefault(verticalData.body),
        leftHeroProps: {
          displayMetadata: columnLeft.displayMetadata,
          displayDate: columnLeft.displayDate,
          displayPrimaryTag: columnLeft.displayPrimaryTag,
          primaryTitle: columnLeft.title,
          secondaryTitle: heDecodeOrDefault(columnLeft.secondaryTitle),
          primaryContentItems: columnLeft.featuredPosts.map((item) =>
            apiToContentItemProps(item)
          ),
          secondaryContentItems: columnLeft.secondaryPosts.map((item) =>
            apiToContentItemProps(item)
          ),
          columnIsHero: false,
        },
        centerHeroProps: {
          displayMetadata: columnCenter.displayMetadata,
          displayDate: columnCenter.displayDate,
          displayPrimaryTag: columnCenter.displayPrimaryTag,
          primaryTitle: columnCenter.title,
          primaryContentItems: columnCenter.posts.map((item) =>
            apiToContentItemProps(item)
          ),
          columnIsHero: true,
          secondaryContentItems: [],
          secondaryTitle: null,
        },
        rightHeroProps: {
          displayMetadata: columnRight.displayMetadata,
          displayDate: columnRight.displayDate,
          displayPrimaryTag: columnRight.displayPrimaryTag,
          primaryTitle: columnRight.title,
          primaryContentItems: columnRight.posts.map((item) =>
            apiToContentItemProps(item)
          ),
          secondaryContentItems: [],
          secondaryTitle: null,
          columnIsHero: false,
        },
      },
      content: apiVerticalToContentProps(verticalData.content),
      meta: apiVerticalToPageMetaData(verticalData),
      readMorePostsProps: verticalData.relatedStories.display
        ? {
            usePaginatedPostsConfig: {
              pageSize: DEFAULT_PAGE_SIZE,
              categoryIdFilter: verticalData.primaryCategory,
              excludePostIdsFilter: verticalData.excludeIds,
            },
            defaultItems: apiToReadMorePosts(posts),
            totalPages,
            title: verticalData.relatedStories.title,
            displayDate: verticalData.relatedStories.displayDate,
            displayKicker: verticalData.relatedStories.displayPrimaryTag,
          }
        : null,
    } satisfies VerticalPageStaticProps,
  };
});

const VerticalsPage: NextPage<
  InferGetServerSidePropsType<typeof getStaticProps>
> = ({ meta, ...props }) => (
  <>
    <PageMeta {...meta} />
    <VerticalPage {...props} />
  </>
);

export default VerticalsPage;
